/**
 * @voltxampere/web — `<vxa-polish-overlay>` (Sprint 57)
 *
 * VISION §6.E P1 karakter polish layer. Top-level fixed overlay
 * that surfaces two interaction effects on top of the canvas:
 *
 *   - snap-glow      — a radial brass-hot glow at the active snap
 *                      target (client-space coords)
 *   - hover-tooltip  — a small HTML pill near the hovered
 *                      component with its label + key parameter
 *
 * Wire vibration (the third pivot-1 effect) lives in
 * `store/polish-effects.ts` as a compute layer; the canvas
 * renderer integration is the Sprint 58 closure for the marathon
 * polish slice (ADR-0092 §S57 documents the split).
 *
 * `prefersReducedMotion` (Sprint 29-5 store/fx-state.ts) suppresses
 * the glow's pulse animation; the static glow remains visible so
 * the affordance is preserved without a moving target.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import { styleMap } from "lit/directives/style-map.js";
import type { ComponentInstance } from "@voltxampere/schema";

import { currentCircuit } from "../store/circuit.js";
import { prefersReducedMotion } from "../store/fx-state.js";
import { hoverTooltipTarget, snapGlowTarget } from "../store/polish-effects.js";
import { t } from "../i18n/index.js";
import { SignalController } from "../lit-utils/signal-controller.js";

@customElement("vxa-polish-overlay")
export class VxaPolishOverlay extends LitElement {
  private readonly _snap = new SignalController(this, snapGlowTarget);
  private readonly _tooltip = new SignalController(this, hoverTooltipTarget);
  private readonly _circuit = new SignalController(this, currentCircuit);
  private readonly _reducedMotion = new SignalController(this, prefersReducedMotion);

  static override styles = css`
    :host {
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: var(--z-canvas-overlay, 5);
    }

    .glow {
      position: absolute;
      border-radius: 50%;
      background: radial-gradient(
        circle,
        rgba(232, 179, 92, 0.55) 0%,
        rgba(232, 179, 92, 0.18) 45%,
        rgba(232, 179, 92, 0) 70%
      );
      transform: translate(-50%, -50%);
      box-shadow: 0 0 12px 4px rgba(232, 179, 92, 0.18);
      transition: opacity 120ms ease;
    }

    .glow[data-pulse] {
      animation: pulse 1.4s ease-in-out infinite;
    }

    @keyframes pulse {
      0%,
      100% {
        transform: translate(-50%, -50%) scale(1);
        opacity: 0.85;
      }
      50% {
        transform: translate(-50%, -50%) scale(1.12);
        opacity: 1;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .glow[data-pulse] {
        animation: none;
        transform: translate(-50%, -50%) scale(1);
        opacity: 0.85;
      }
    }

    .tooltip {
      position: absolute;
      transform: translate(12px, 12px);
      padding: 4px 8px;
      background: rgba(8, 12, 22, 0.92);
      border: 1px solid var(--brass-faint, #6b5230);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
      pointer-events: none;
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.12em;
      color: var(--ivory, #f5efe6);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      white-space: nowrap;
      max-width: 260px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .tooltip .name {
      color: var(--brass, #d4a857);
    }

    .tooltip .sep {
      color: var(--ivory-faint, #7a7264);
      margin: 0 0.4em;
    }
  `;

  override render() {
    // Static wrapper around the two dynamic slots — keeps Lit's
    // template parser happy in environments (notably happy-dom) where
    // a dynamic-only template renders as an inert `<?>` placeholder.
    return html`<div data-overlay="polish">${this.renderSnapGlow()}${this.renderTooltip()}</div>`;
  }

  private renderSnapGlow() {
    const target = this._snap.value;
    if (target === null) return nothing;
    const radius = 14 + target.intensity * 18;
    const size = `${String(radius * 2)}px`;
    const pulse = !this._reducedMotion.value;
    const styles = styleMap({
      left: `${String(target.point.x)}px`,
      top: `${String(target.point.y)}px`,
      width: size,
      height: size,
    });
    return html`<div
      class="glow"
      data-testid="polish-snap-glow"
      ?data-pulse=${pulse}
      style=${styles}
    ></div>`;
  }

  private renderTooltip() {
    const target = this._tooltip.value;
    if (target === null) return nothing;
    const circuit = this._circuit.value;
    if (circuit === null) return nothing;
    const component = circuit.components.find((c) => c.id === target.componentId);
    if (component === undefined) return nothing;
    const styles = styleMap({
      left: `${String(target.clientX)}px`,
      top: `${String(target.clientY)}px`,
    });
    return html`<div
      class="tooltip"
      data-testid="polish-hover-tooltip"
      data-component-id=${component.id}
      style=${styles}
    >
      <span class="name">${component.label ?? component.id}</span>
      <span class="sep">·</span>
      <span>${describeComponent(component)}</span>
    </div>`;
  }
}

function describeComponent(component: ComponentInstance): string {
  const type = component.type;
  const params = component.params;
  // Try to surface the canonical "headline" param per known type.
  const headline = headlineParam(type, params);
  if (headline !== null) return `${typeLabel(type)} · ${headline}`;
  return typeLabel(type);
}

function typeLabel(type: string): string {
  // Lightweight TR/EN-friendly label — full i18n catalog lives in
  // the component library (Sprint 18); the tooltip uses a compact
  // fallback so it can render even before the user opens the
  // component library pane.
  switch (type) {
    case "resistor":
      return t("polish.tooltip.type.resistor");
    case "capacitor":
      return t("polish.tooltip.type.capacitor");
    case "inductor":
      return t("polish.tooltip.type.inductor");
    case "voltage-source":
    case "dc-source":
      return t("polish.tooltip.type.voltage-source");
    case "ac-source":
      return t("polish.tooltip.type.ac-source");
    case "diode":
      return t("polish.tooltip.type.diode");
    case "led":
      return t("polish.tooltip.type.led");
    case "bjt-npn":
      return t("polish.tooltip.type.bjt-npn");
    case "bjt-pnp":
      return t("polish.tooltip.type.bjt-pnp");
    case "op-amp":
    case "opamp":
      return t("polish.tooltip.type.opamp");
    case "ground":
      return t("polish.tooltip.type.ground");
    default:
      return type;
  }
}

function headlineParam(type: string, params: ComponentInstance["params"]): string | null {
  const raw = (key: string): unknown => params[key];
  switch (type) {
    case "resistor":
      return formatScalar(raw("resistance"), "Ω");
    case "capacitor":
      return formatScalar(raw("capacitance"), "F");
    case "inductor":
      return formatScalar(raw("inductance"), "H");
    case "voltage-source":
    case "dc-source":
      return formatScalar(raw("voltage"), "V");
    case "ac-source":
      return formatScalar(raw("amplitude"), "V");
    case "led":
      return formatScalar(raw("vForward") ?? raw("forwardVoltage"), "V");
    default:
      return null;
  }
}

function formatScalar(value: unknown, unit: string): string | null {
  let n: number | null = null;
  if (typeof value === "number" && Number.isFinite(value)) n = value;
  else if (
    typeof value === "object" &&
    value !== null &&
    "value" in value &&
    typeof value.value === "number"
  ) {
    n = (value as { value: number }).value;
  }
  if (n === null) return null;
  const abs = Math.abs(n);
  if (abs === 0) return `0 ${unit}`;
  const log = Math.floor(Math.log10(abs) / 3);
  const PREFIXES: Readonly<Record<number, string>> = {
    [-4]: "p",
    [-3]: "n",
    [-2]: "µ",
    [-1]: "m",
    [0]: "",
    [1]: "k",
    [2]: "M",
    [3]: "G",
  };
  const prefix = PREFIXES[log] ?? "";
  const scaled = n / 10 ** (log * 3);
  const display = Number.isInteger(scaled)
    ? scaled.toFixed(0)
    : scaled.toPrecision(3).replace(/\.?0+$/, "");
  return `${display} ${prefix}${unit}`;
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-polish-overlay": VxaPolishOverlay;
  }
}
